import { DeleteOutlined, HolderOutlined, PlusOutlined } from "@ant-design/icons"
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Button, Input } from "antd"
import React from "react"
import { t } from "~/languages/i18n"

interface FaqItem {
  id: string
  question: string
  answer: string
}

interface SortableFaqItemProps {
  id: string
  item: FaqItem
  onUpdate: (id: string, field: "question" | "answer", value: string) => void
  onRemove: (id: string) => void
}

const SortableFaqItem = ({ id, item, onUpdate, onRemove }: SortableFaqItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} className="flex gap-2 items-start p-3 mb-2 bg-white border border-gray-200 rounded-lg shadow-sm">
      <div {...attributes} {...listeners} className="cursor-grab text-gray-400 font-medium p-1 hover:bg-gray-100 rounded mt-1">
        <HolderOutlined />
      </div>
      <div className="flex-1 flex flex-col gap-2">
        <div>
          <label className="text-sm font-medium text-gray-600 mb-1 block">{t("Question")}</label>
          <Input
            value={item.question}
            onChange={e => onUpdate(id, "question", e.target.value)}
            placeholder={t("Enter question")}
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 mb-1 block">{t("Answer")}</label>
          <Input.TextArea
            value={item.answer}
            onChange={e => onUpdate(id, "answer", e.target.value)}
            placeholder={t("Enter answer")}
            rows={3}
          />
        </div>
      </div>
      <Button
        type="text"
        danger
        icon={<DeleteOutlined />}
        onClick={() => onRemove(id)}
        className="mt-7"
      />
    </div>
  )
}

interface FaqEditorProps {
  faqs: FaqItem[]
  onChange: (faqs: FaqItem[]) => void
}

const FaqEditor: React.FC<FaqEditorProps> = ({ faqs, onChange }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = faqs.findIndex(f => f.id === active.id)
      const newIndex = faqs.findIndex(f => f.id === over.id)
      if (oldIndex !== -1 && newIndex !== -1) {
        onChange(arrayMove(faqs, oldIndex, newIndex))
      }
    }
  }

  const handleAddFaq = () => {
    const newFaq: FaqItem = {
      id: `faq-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      question: "",
      answer: "",
    }
    onChange([...faqs, newFaq])
  }

  const handleUpdateFaq = (id: string, field: "question" | "answer", value: string) => {
    onChange(faqs.map(faq => (faq.id === id ? { ...faq, [field]: value } : faq)))
  }

  const handleRemoveFaq = (id: string) => {
    onChange(faqs.filter(faq => faq.id !== id))
  }

  return (
    <div className="faq-editor mt-4 mb-4 p-4 border border-blue-200 rounded-lg bg-blue-50">
      <div className="flex justify-between items-center mb-4">
        <span className="text-base font-medium text-blue-700">{t("FAQs")}</span>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddFaq}>
          {t("Add FAQ")}
        </Button>
      </div>

      {faqs.length === 0 ? (
        <div className="text-center text-gray-500 py-4">
          {t("No FAQs added yet. Click 'Add FAQ' to create one.")}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={faqs.map(f => f.id)} strategy={verticalListSortingStrategy}>
            {faqs.map(item => (
              <SortableFaqItem
                key={item.id}
                id={item.id}
                item={item}
                onUpdate={handleUpdateFaq}
                onRemove={handleRemoveFaq}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </div>
  )
}

export default FaqEditor
export type { FaqItem }
